'use client';

import { useEffect, useRef, type ReactNode } from 'react';

/**
 * HEADER CTA PULSE: a small version of the hero's Magnet scene for the header's Book Strategy Call key.
 * Every ~9 seconds, one short burst (~1.7 s): a dashed orbit ring settles around the key, three "lead" particles with comet tails
 * spiral in and are absorbed, the key gives one small kick, a ring flashes out from it and a light sweep crosses the label.
 * Between bursts nothing animates (the old always-on header sheen was removed for speed, so this one runs only on a timer).
 *
 * PREVIEW SWITCH (owner review on the real site, 2026-10-15): it shows only while <html data-ctafx> is set.
 *   ?ctafx=on   turn it on for this tab (remembered in sessionStorage, follows you page to page)
 *   ?ctafx=off  turn it off
 * Motion: transform and opacity only. No burst while the tab is hidden, while the key is not displayed (the header renders three
 * responsive copies), or for visitors who ask for reduced motion.
 */
const FIRST_MS = 2400;
const EVERY_MS = 9000;
const BURST_MS = 1800;
const KEY = 'lp-ctafx';

function readSwitch() {
  try {
    const q = new URLSearchParams(window.location.search).get('ctafx');
    if (q === 'on') sessionStorage.setItem(KEY, '1');
    if (q === 'off') sessionStorage.removeItem(KEY);
    if (sessionStorage.getItem(KEY) === '1') document.documentElement.setAttribute('data-ctafx', '');
    else document.documentElement.removeAttribute('data-ctafx');
  } catch { /* storage blocked: the preview stays off */ }
}

export function HeaderCtaFx({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    readSwitch();
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let off: ReturnType<typeof setTimeout> | undefined;
    const burst = () => {
      if (!document.documentElement.hasAttribute('data-ctafx') || reduce.matches || document.hidden || el.offsetParent === null) return;
      el.removeAttribute('data-go');
      void el.offsetWidth; // restart the keyframes cleanly
      el.setAttribute('data-go', '');
      clearTimeout(off);
      off = setTimeout(() => el.removeAttribute('data-go'), BURST_MS);
    };
    let every: ReturnType<typeof setInterval> | undefined;
    const first = setTimeout(() => { burst(); every = setInterval(burst, EVERY_MS); }, FIRST_MS);
    return () => { clearTimeout(first); clearInterval(every); clearTimeout(off); };
  }, []);

  return (
    <span ref={ref} className="hcx">
      {children}
      <span className="hcx-f" aria-hidden="true">
        <span className="hcx-orbit" />
        <span className="hcx-ring" />
        <i /><i /><i />
      </span>
    </span>
  );
}

/** The light sweep sits inside the key (the key clips it); place it as the link's last child. */
export function HeaderCtaSweep() {
  return <span className="hcx-sw" aria-hidden="true" />;
}

export const HEADER_CTA_FX_CSS = `
.hcx{position:relative;display:inline-flex}
.hcx-f,.hcx-sw{display:none}
html[data-ctafx] .hcx-f{display:block;position:absolute;inset:0;pointer-events:none;z-index:2}
html[data-ctafx] .hcx > a{position:relative;overflow:hidden}
html[data-ctafx] .hcx-sw{display:block;position:absolute;inset:0;border-radius:inherit;pointer-events:none;overflow:hidden}
.hcx-sw::before{content:'';position:absolute;top:-20%;bottom:-20%;left:0;width:38%;transform:translateX(-160%) skewX(-18deg);opacity:0;
  background:linear-gradient(90deg,transparent,rgba(255,255,255,.75),transparent)}
.hcx-orbit,.hcx-ring{position:absolute;inset:-7px -12px;border-radius:999px;opacity:0}
.hcx-orbit{border:1.5px dashed rgba(124,92,255,.85)}
.hcx-ring{inset:-2px;border:2px solid rgba(var(--sk-cta-rgb,182,255,92),.95);box-shadow:0 0 14px rgba(var(--sk-cta-rgb,182,255,92),.7)}
html[data-skin-mode="light"] .hcx-orbit{border-color:rgba(91,61,245,.75)}
.hcx-f i{position:absolute;left:0;top:0;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;opacity:0;offset-rotate:auto;offset-distance:0%}
.hcx-f i::before{content:'';position:absolute;right:3px;top:1px;width:16px;height:4px;border-radius:4px;background:linear-gradient(90deg,transparent,currentColor);opacity:.7}
.hcx-f i:nth-child(3){color:#ff34a7;background:#ff34a7;box-shadow:0 0 8px #ff34a7;offset-path:path('M -60 0 C -38 -8, -8 0, 16 20')}
.hcx-f i:nth-child(4){color:#7c5cff;background:#7c5cff;box-shadow:0 0 8px #7c5cff;offset-path:path('M -66 30 C -42 40, -12 34, 16 20')}
.hcx-f i:nth-child(5){color:#22e4ff;background:#22e4ff;box-shadow:0 0 8px #22e4ff;offset-path:path('M -40 54 C -22 56, 2 44, 16 20')}
html[data-skin-mode="light"] .hcx-f i:nth-child(5){color:#0a8fb0;background:#0a8fb0;box-shadow:0 0 8px rgba(10,143,176,.8)}
@media (prefers-reduced-motion:no-preference){
  html[data-ctafx] .hcx[data-go] .hcx-orbit{animation:hcx-orbit 1s cubic-bezier(.2,.7,.2,1) both}
  html[data-ctafx] .hcx[data-go] .hcx-f i{animation:hcx-in .62s cubic-bezier(.55,0,.7,.4) both}
  html[data-ctafx] .hcx[data-go] .hcx-f i:nth-child(3){animation-delay:.12s}
  html[data-ctafx] .hcx[data-go] .hcx-f i:nth-child(4){animation-delay:.22s}
  html[data-ctafx] .hcx[data-go] .hcx-f i:nth-child(5){animation-delay:.32s}
  html[data-ctafx] .hcx[data-go] > a{animation:hcx-kick .42s cubic-bezier(.3,1.6,.5,1) .9s both}
  html[data-ctafx] .hcx[data-go] .hcx-ring{animation:hcx-ring .75s ease-out .92s both}
  html[data-ctafx] .hcx[data-go] .hcx-sw::before{animation:hcx-sweep .8s ease-in-out .95s both}
}
@keyframes hcx-orbit{0%{opacity:0;transform:scale(1.28,1.5) rotate(-12deg)}30%{opacity:.9}80%{opacity:.5;transform:scale(1) rotate(6deg)}100%{opacity:0;transform:scale(.97) rotate(8deg)}}
@keyframes hcx-in{0%{offset-distance:0%;opacity:0;transform:scale(.6)}20%{opacity:1;transform:scale(1)}85%{opacity:1}100%{offset-distance:100%;opacity:0;transform:scale(.4)}}
@keyframes hcx-kick{0%{transform:scale(1)}40%{transform:scale(1.07)}100%{transform:scale(1)}}
@keyframes hcx-ring{0%{opacity:0;transform:scale(1)}4%{opacity:.95;transform:scale(1)}100%{opacity:0;transform:scale(1.18,1.45)}}
@keyframes hcx-sweep{0%{opacity:1;transform:translateX(-160%) skewX(-18deg)}100%{opacity:1;transform:translateX(330%) skewX(-18deg)}}
`;
